'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { isEqual } from 'lodash'

import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useCatalogContext } from '@marketplace-web/catalog/catalog-page-data'
import { useLatestCallback } from '@marketplace-web/shared/ui-state-util'
import {
  getClosetPromotions,
  getClosetPromotionsSvcVas,
  ClosetModel,
  VasEntryPoint,
  parseCatalogUrlParams,
  transformClosetsSvcVas,
  transformClosets,
  ClosetDtoSvcVas,
  ClosetDto,
} from '@marketplace-web/vas/vas-data'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'

import useFetchVasEntryPoints from './useFetchVasEntryPoints'

const CLOSET_PROMOTIONS_COUNT = 4

const useClosetPromotion = () => {
  const searchSessionId = useCatalogContext().state.items.searchTrackingParams?.sessionId
  const vasApiGatewaySwapPromotedClosetsAbTest = useAbTest('vas_api_gateway_swap_promoted_closets')
  const isVasApiGatewaySwapPromotedClosetsAbTestOn =
    vasApiGatewaySwapPromotedClosetsAbTest?.variant === 'on'
  const trackExpose = useTrackAbTestCallback()

  const { searchParams, relativeUrl } = useBrowserNavigation()

  const currentFilterParams = parseCatalogUrlParams(searchParams, relativeUrl)
  const previousFilterParamsRef = useRef(currentFilterParams)

  const stableFilterParams = useMemo(() => {
    if (!isEqual(currentFilterParams, previousFilterParamsRef.current)) {
      previousFilterParamsRef.current = currentFilterParams

      return currentFilterParams
    }

    return previousFilterParamsRef.current
  }, [currentFilterParams])
  const searchByImageUuid = searchParams.search_by_image_uuid

  let isClosetPromotionKillswitchEnabled = useFeatureSwitch('killswitch_closet_promotion_web')
  // search by image feature does not support closet promotions
  isClosetPromotionKillswitchEnabled ||= !!searchByImageUuid

  const userIds = useRef<Array<number>>([])
  const hasMore = useRef(true)

  const [closets, setClosets] = useState<Array<ClosetModel>>([])

  const resetClosets = () => {
    userIds.current = []
    hasMore.current = true
  }

  useEffect(() => {
    resetClosets()
  }, [stableFilterParams])

  const { data: vasEntryPoints } = useFetchVasEntryPoints([VasEntryPoint.PromotedClosets])

  const fetchClosetPromotions = useLatestCallback(async () => {
    const shouldFetch = !isClosetPromotionKillswitchEnabled && hasMore.current

    if (!shouldFetch) {
      setClosets([])

      return
    }

    const response = isVasApiGatewaySwapPromotedClosetsAbTestOn
      ? await getClosetPromotionsSvcVas({
          perPage: CLOSET_PROMOTIONS_COUNT,
          screenName: 'catalog',
          excludedUserIds: userIds.current,
          searchSessionId,
          catalogFilterParams: stableFilterParams,
        })
      : await getClosetPromotions({
          perPage: CLOSET_PROMOTIONS_COUNT,
          screenName: 'catalog',
          excludedUserIds: userIds.current,
          searchSessionId,
          catalogFilterParams: stableFilterParams,
        })

    trackExpose(vasApiGatewaySwapPromotedClosetsAbTest)

    if ('errors' in response || 'error' in response) {
      setClosets([])

      return
    }

    hasMore.current = response.page_info.has_more
    const transformedClosets = isVasApiGatewaySwapPromotedClosetsAbTestOn
      ? transformClosetsSvcVas(response.promoted_closets as Array<ClosetDtoSvcVas>)
      : transformClosets(response.promoted_closets as Array<ClosetDto>)

    if (transformedClosets[0]) {
      transformedClosets[0].showBanner = true
    }

    userIds.current = [...userIds.current, ...transformedClosets.map(closet => closet.user.id)]
    setClosets(transformedClosets)
  })

  useEffect(() => {
    fetchClosetPromotions()
  }, [searchSessionId, fetchClosetPromotions])

  return { closetsWithBanner: closets, vasEntryPoints }
}

export default useClosetPromotion
